<template>
    <div class="conference-room h-100">
        <div
            class="
                box-shadow-h
                w-100
                is-flex is-justify-content-space-between is-align-items-baseline
                has-text-primary
                pl-4
                pt-2
                pb-2
            "
        >
            <h6 class="is-subtitle-size is-primary mr-5">
                {{ conferenceName }}
                <a class="is-pointer mr-1" @click="copyClipboard">
                    <Icon family="fal" name="fa-copy" />
                </a>
            </h6>
            <div>
                <RecordButton v-if="isLogged" @record="toggleRecord" :initRecording="isRecording"/>
                <button
                    v-if="isLogged"
                    class="button is-danger mr-3"
                    @click="toggleCloseRoomConfirm"
                    :disabled="isRecord"
                >
                    <Icon family="fa" name="fa-stop-circle" />
                    <span> Finalizar trámite </span>
                </button>
            </div>
        </div>
        <section class="conference-area">
            <div class="columns is-gapless h-100">
                <div class="column">
                    <Conference
                        v-if="startConference"
                        ref="conference"
                        :user="user"
                        :connections="users"
                        :roomId="roomId"
                        :signalRSocket="signalRSocket"
                    />
                </div>

                <div v-if="isChatOpen" class="column is-2 box-shadow-v">
                    <div class="tabs">
                        <ul class="is-justify-content-center">
                            <li
                                class="has-text-primary"
                                :class="{ 'is-active': displayMessages }"
                                @click="toggleMessages"
                            >
                                <a
                                    >{{ $t("chat.messages")
                                    }}<Icon
                                        family="fal"
                                        name="fa-comment-lines"
                                /></a>
                            </li>
                            <li
                                class="has-text-grey-medium"
                                :class="{ 'is-active': !displayMessages }"
                                @click="toggleMessages"
                            >
                                <a
                                    >{{ $t("chat.users")
                                    }}<Icon family="fal" name="fa-users fa-lg"
                                /></a>
                            </li>
                        </ul>
                    </div>
                    <div class="h-90" v-if="displayMessages">
                        <Chat
                            v-if="signalRSocket"
                            :roomId="roomId"
                            :signalRSocket="signalRSocket"
                        />
                        <div v-else>
                            <Loading />
                        </div>
                    </div>
                    <div v-else class="pl-2">
                        <UsersList />
                    </div>
                </div>
            </div>
        </section>
        <CloseRoomModal
            v-if="showCloseRoom"
            :roomId="roomId"
            @close="toggleCloseRoomConfirm"
            @closed="closedRoom"
        />
        <FinishModal v-if="showFinishModal" :roomId="roomId" />
        <ShareRoomModal v-if="showCloseShareRoom" @close="shareModal" />
        <RecordNotificationModal v-if="isLogged" :roomId="roomId" :display="showRecordNotificationModal && !isRecord" @record="toggleRecord"/>
    </div>
</template>

<script>
import Conference from "@/components/conference/Conference.vue";
import Chat from "@/components/chat/Chat.vue";
import UsersList from "@/components/users/UsersList.vue";
import Loading from "@/components/Loading.vue";
import CloseRoomModal from "@/components/conference/CloseRoomModal";
import FinishModal from "@/components/conference/FinishModal";
import RecordNotificationModal from "@/components/conference/RecordNotificationModal";
import RecordButton from "@/components/conference/RecordButton";
import ShareRoomModal from "@/components/conference/ShareRoomModal";

import * as SignalR from "@microsoft/signalr";

import { RepositoryFactory } from "@/services/api";
import { mapActions, mapGetters } from "vuex";

export default {
    name: "ConferenceRoom",
    components: {
        Conference,
        Chat,
        UsersList,
        Loading,
        CloseRoomModal,
        FinishModal,
        RecordNotificationModal,
        RecordButton,
        ShareRoomModal,
    },
    props: {
        roomId: { type: String, required: true },
    },
    data() {
        return {
            conferenceName: "",
            ping: null,
            connectionState: "",
            localStream: null,
            user: {},
            users: {},
            interval: null,
            signalRSocket: false,

            startConference: false,

            displayMessages: true,
            showCloseRoom: false,
            showFinishModal: false,
            showRecordNotificationModal: false,

            showCloseShareRoom: false,
            isRecord: false,
        };
    },
    computed: {
        ...mapGetters("conference", [
            "getUserName",
            "getUserIdentification",
            "getMediaStreamConstraints",
        ]),
        ...mapGetters("chat", ["isChatOpen", "isUsersOpen"]),
        ...mapGetters("oidcStore", ["oidcIsAuthenticated"]),
        isLogged() {
            return this.oidcIsAuthenticated;
        },
        userName() {
            return this.getUserName;
        },
        mediaStreamConstraints() {
            return this.getMediaStreamConstraints;
        },
        userIdentification() {
            return this.getUserIdentification;
        },
        isRecording() {
            console.log("exec is recording", this.isRecord);
            return this.isRecord;
        }
    },
    async created() {
        if (this.userName === undefined || this.userName === "") {
            window.console.log("Redirect to room configs");

            let processId = this.$route.query.processId;
            if (processId) {
                this.$router.push({
                    name: "room",
                    params: { roomId: this.roomId },
                    query: {
                        processId: processId,
                        cedula: this.$route.query.cedula,
                        environment: this.$route.query.environment,
                    },
                });
            } else {
                this.$router.push({
                    name: "room",
                    params: { roomId: this.roomId },
                    query: { require_auth: true },
                });
            }
        }
    },
    async beforeDestroy() {
        clearInterval(this.interval);
        if (this.localStream != null) {
            this.localStream.getTracks().forEach((track) => track.stop());
        }
        if (this.signalRSocket !== false) {
            let socket = this.signalRSocket;
            this.signalRSocket = null;
            await socket.stop();
        }
    },
    async mounted() {
        // Check room closed
        let response = await RepositoryFactory.conference.get({
            roomId: this.roomId,
        });
        let room = response.data;
        if (room.state === 1) {
            this.showFinishModal = true;
            return;
        }

        if (this.userName !== undefined && this.userName !== "") {
            this.clearMessages();

            this.localStream = await navigator.mediaDevices.getUserMedia(
                this.mediaStreamConstraints
            );

            window.console.log("init socket");
            await this.signalRInitialize();
            if (this.signalRSocket) {
                this.signalRSocket.on("Joined", this.signalRJoined);
                this.signalRSocket.on("NotifyJoin", this.signalRNotifyJoin);
                this.signalRSocket.on("Left", this.signalRLeft);
                this.signalRSocket.on(
                    "NotifyRoomClosed",
                    this.signalRNotifyRoomClosed
                );

                this.signalRSocket.on("SendUsers", this.signalRSendUsers);

                this.signalRSocket.on("ReceiveEvent", this.signalRReceiveEvent);

                await this.signalRSocket.invoke(
                    "Join",
                    this.roomId,
                    this.userName,
                    this.userIdentification
                );

                if (this.isLogged) {
                    await RepositoryFactory.operators.notifyJoinRoom({
                        roomId: this.roomId,
                    });
                } else {
                    await RepositoryFactory.onlineUser.notifyJoinRoom({
                        userId: this.$route.query.cedula,
                        processId: this.$route.query.processId,
                    });
                }
            }

            this.conferenceName = response.data.name;
            this.setConferenceId({ roomId: this.roomId });
            this.setConferenceName({ name: this.conferenceName });
        } else {
            window.console.log("wating to redirection");
        }
    },
    methods: {
        ...mapActions("chat", ["clearMessages", "addMessage"]),
        ...mapActions("conference", [
            "setConferenceId",
            "setConferenceName",
            "setParticipantId",
            "updateUsers",
        ]),
        ...mapActions("historical", ["addEvent"]),
        async signalRInitialize() {
            try {
                const socket = new SignalR.HubConnectionBuilder()
                    .withUrl(process.env.VUE_APP_CHAT_HUB)
                    .withAutomaticReconnect()
                    .build();

                socket.connection.onclose = async (error) => {
                    if (error !== undefined) {
                        window.console.log(error);
                    }

                    if (this.signalRSocket !== null) {
                        window.console.log("Reconnecting...");
                        this.signalRSocket = false;
                        await this.signalRInitialize();
                    }
                };

                await socket.start();

                this.signalRSocket = socket;
            } catch (err) {
                window.console.log("Error, reconnecting...");

                setTimeout(() => {
                    this.signalRInitialize();
                }, 1000);
            }
        },
        async signalRJoined(data) {
            this.$log({ method: "Joined", data: data.name });
            this.users[data.id] = data;

            this.addMessage({ notification: `${data.name} se ha unido.` });
            this.signalRSocket.invoke("SendUsers", this.roomId);
        },
        signalRNotifyJoin(data) {
            this.setParticipantId({ id: data.id });

            this.user = {
                id: data.id,
                name: data.name,
                identification: data.userIdentification,
            };
            this.users[this.userName] = this.user;

            this.startConference = true;
            this.showRecordNotificationModal = true;
        },
        signalRLeft(participantId) {
            this.$log({ method: "Left", data: participantId });
            let userName = this.users[participantId].name;
            delete this.users[participantId];
            this.addMessage({ notification: `${userName} se ha ido.` });
            this.signalRSocket.invoke("SendUsers", this.roomId);
        },
        signalRSendUsers(users) {
            window.console.log("signalRSendUsers");
            window.console.log(users);
            this.updateUsers(users);
        },
        signalRReceiveEvent(event) {
            window.console.log("signalRReceiveEvent");
            this.addEvent(event);
        },
        async copyClipboard() {
            await navigator.clipboard.writeText(window.location.href);
        },
        toggleMessages() {
            this.displayMessages = !this.displayMessages;
        },
        toggleCloseRoomConfirm() {
            this.showCloseRoom = !this.showCloseRoom;
        },
        closedRoom() {
            this.signalRSocket.invoke("RoomClosed", this.roomId);
        },
        signalRNotifyRoomClosed() {
            this.showCloseRoom = false;
            this.showFinishModal = true;
        },
        shareModal() {
            this.showCloseShareRoom = !this.showCloseShareRoom;
        },
        toggleRecord(isRecord) {
            this.isRecord = isRecord;
            this.showRecordNotificationModal = isRecord;

            console.log("exec toggleRecord with status:", this.isRecord);
        },
    },
};
</script>

<style lang="scss">
.conference-room {
    & > .conference-area {
        height: calc(100% - 3.5rem);
    }
}

.blink_me {
    animation: blinker 1.5s linear infinite;
}

@keyframes blinker {
    50% {
        opacity: 0;
    }
}
</style>